import React from 'react';
import { Box, HStack, Text, IconButton, Badge, Tooltip } from '@chakra-ui/react';
import { CheckIcon, TimeIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import type { NotificationItem as Item } from '../../types/notification';
import RelativeTimeText from '../RelativeTimeText';

export default function NotificationItem({
  item,
  onClick,
  onToggleRead,
  onArchive,
  highlighted,
}: {
  item: Item;
  /** Opens the row. The caller owns where it routes (role-resolved target). */
  onClick: () => void;
  /** Toggles read/unread. Never navigates. */
  onToggleRead: () => void;
  onArchive: () => void;
  highlighted?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const unread = item.readAt == null;

  return (
    <Box
      px={3}
      py={2}
      bg={unread ? 'blue.50' : 'transparent'}
      _hover={{ bg: unread ? 'blue.100' : 'gray.50' }}
      cursor="pointer"
      borderBottomWidth="1px"
      data-highlighted={highlighted ? 'true' : undefined}
      outline={highlighted ? '2px solid' : undefined}
      outlineColor={highlighted ? 'blue.400' : undefined}
      onClick={onClick}
    >
      <HStack justify="space-between" align="flex-start">
        <Box flex="1" minW={0}>
          <HStack spacing={2}>
            {unread && <Badge colorScheme="blue" variant="solid" boxSize="8px" borderRadius="full" />}
            <Text fontWeight={unread ? 'semibold' : 'normal'} fontSize="sm" isTruncated>
              {item.title}
            </Text>
          </HStack>
          <Text fontSize="xs" color="gray.600" noOfLines={2}>
            {item.body}
          </Text>
          <HStack spacing={2} mt={1} color="gray.400" fontSize="xs">
            <TimeIcon boxSize={3} />
            <RelativeTimeText date={item.occurredAt} />
            {item.classroomCode && <Text>· {item.classroomCode}</Text>}
          </HStack>
        </Box>
        <HStack spacing={1} flexShrink={0}>
          <Tooltip label={unread ? t('notification.markRead') : t('notification.markUnread')}>
            <IconButton
              aria-label={unread ? t('notification.markRead') : t('notification.markUnread')}
              icon={<CheckIcon />}
              size="xs"
              variant="ghost"
              colorScheme={unread ? 'gray' : 'blue'}
              onClick={(e) => {
                e.stopPropagation();
                onToggleRead();
              }}
            />
          </Tooltip>
          <Tooltip label={t('notification.archive')}>
            <IconButton
              aria-label={t('notification.archive')}
              icon={<Box as="span">🗄</Box>}
              size="xs"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onArchive();
              }}
            />
          </Tooltip>
        </HStack>
      </HStack>
    </Box>
  );
}
